import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Loader2 } from "lucide-react";

const EMPTY = {
  title: "", description: "", video_url: "", thumbnail_url: "",
  season: 1, episode_number: 1, duration_seconds: 0,
  is_premium: true, show: "Boyz Gone Wild",
};

export default function Admin() {
  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/episodes");
    setEpisodes(data);
  };

  useEffect(() => { refresh(); }, []);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        season: Number(form.season) || 1,
        episode_number: Number(form.episode_number) || 1,
        duration_seconds: Number(form.duration_seconds) || 0,
      };
      if (editingId) {
        await api.put(`/admin/episodes/${editingId}`, payload);
        toast.success("Episode updated");
      } else {
        await api.post("/admin/episodes", payload);
        toast.success("Episode created");
      }
      setForm(EMPTY);
      setEditingId(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ep) => {
    setEditingId(ep.id);
    setForm({
      title: ep.title, description: ep.description || "",
      video_url: ep.video_url || "", thumbnail_url: ep.thumbnail_url || "",
      season: ep.season, episode_number: ep.episode_number,
      duration_seconds: ep.duration_seconds || 0,
      is_premium: ep.is_premium ?? true, show: ep.show || "Boyz Gone Wild",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this episode?")) return;
    try {
      await api.delete(`/admin/episodes/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadThumb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload/thumbnail", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}`;
      setField("thumbnail_url", url);
      toast.success("Thumbnail uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      <Navbar />
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 pt-10 pb-24">
        <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-2">Admin</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Content management</h1>

        <form onSubmit={submit} className="mt-10 border border-white/10 bg-brand-surface p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-episode-form">
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-3">
              {editingId ? `Editing episode` : "Add a new episode"}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Title</Label>
            <Input value={form.title} onChange={e => setField("title", e.target.value)} required
              data-testid="admin-title" className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setField("description", e.target.value)}
              data-testid="admin-description" rows={3}
              className="mt-1.5 bg-black border-white/10 rounded-none" />
          </div>
          <div>
            <Label>Show</Label>
            <Input value={form.show} onChange={e => setField("show", e.target.value)}
              data-testid="admin-show" className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>
          <div>
            <Label>Video URL (mp4 / HLS)</Label>
            <Input value={form.video_url} onChange={e => setField("video_url", e.target.value)} required
              data-testid="admin-video-url"
              placeholder="https://…/episode.mp4"
              className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>

          <div>
            <Label>Thumbnail URL</Label>
            <Input value={form.thumbnail_url} onChange={e => setField("thumbnail_url", e.target.value)}
              data-testid="admin-thumbnail-url" placeholder="https://…/poster.jpg"
              className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>
          <div>
            <Label>Upload thumbnail (optional)</Label>
            <label className="mt-1.5 flex items-center gap-2 bg-black border border-white/10 px-3 h-11 cursor-pointer" data-testid="admin-thumb-upload-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span className="text-sm text-brand-muted">{uploading ? "Uploading…" : "Choose image"}</span>
              <input type="file" accept="image/*" onChange={uploadThumb} className="hidden" data-testid="admin-thumb-upload-input" />
            </label>
          </div>

          <div>
            <Label>Season</Label>
            <Input type="number" min={1} value={form.season} onChange={e => setField("season", e.target.value)}
              data-testid="admin-season" className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>
          <div>
            <Label>Episode #</Label>
            <Input type="number" min={1} value={form.episode_number} onChange={e => setField("episode_number", e.target.value)}
              data-testid="admin-episode-number" className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>
          <div>
            <Label>Duration (sec)</Label>
            <Input type="number" min={0} value={form.duration_seconds} onChange={e => setField("duration_seconds", e.target.value)}
              data-testid="admin-duration" className="mt-1.5 bg-black border-white/10 rounded-none h-11" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_premium} onCheckedChange={(v) => setField("is_premium", v)} data-testid="admin-premium-switch" />
            <Label className="!m-0">Premium (requires subscription)</Label>
          </div>

          <div className="md:col-span-2 flex gap-3 mt-2">
            <Button type="submit" disabled={saving}
              data-testid="admin-save-btn"
              className="bg-brand-primary hover:bg-brand-primaryHover rounded-none h-12 px-7 uppercase tracking-[0.15em] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? "Update episode" : "Create episode")}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" data-testid="admin-cancel-btn"
                onClick={() => { setEditingId(null); setForm(EMPTY); }}
                className="border-white/20 bg-white/5 text-white rounded-none h-12 px-7 uppercase tracking-[0.15em]">
                Cancel
              </Button>
            )}
          </div>
        </form>

        <div className="mt-12">
          <h2 className="font-display text-2xl md:text-3xl uppercase mb-5">Library ({episodes.length})</h2>
          <div className="border border-white/10">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.18em] text-brand-muted border-b border-white/10 bg-black/40">
                <tr>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">S/E</th>
                  <th className="text-left p-3 hidden md:table-cell">Show</th>
                  <th className="text-left p-3 hidden md:table-cell">Premium</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {episodes.map(ep => (
                  <tr key={ep.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-row-${ep.id}`}>
                    <td className="p-3 font-medium">{ep.title}</td>
                    <td className="p-3 text-brand-muted">S{ep.season} E{ep.episode_number}</td>
                    <td className="p-3 text-brand-muted hidden md:table-cell">{ep.show}</td>
                    <td className="p-3 text-brand-muted hidden md:table-cell">{ep.is_premium ? "Yes" : "No"}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(ep)} data-testid={`admin-edit-${ep.id}`} className="hover:bg-white/10 rounded-none">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(ep.id)} data-testid={`admin-delete-${ep.id}`} className="hover:bg-red-500/20 text-red-300 rounded-none">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {episodes.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-brand-muted">No episodes yet. Create the first one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
